export const OFFER_CITY_OVERRIDE_STORAGE_KEY = 'offers_city_override';

const parseJsonIfNeeded = (value) => {
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch (_error) {
    return value;
  }
};

export const extractPayloadValue = (payload) => {
  if (payload?.value !== undefined) {
    return parseJsonIfNeeded(payload.value);
  }
  return payload;
};

const toBoolean = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  const normalized = String(value || '').trim().toLowerCase();
  return ['true', '1', 'yes', 'y'].includes(normalized);
};

const safeText = (value, fallback = '') => {
  if (value === null || value === undefined) return fallback;
  const text = String(value).trim();
  return text || fallback;
};

const normalizeCategory = (value = '') => {
  const normalized = String(value || '').trim().toUpperCase().replace(/[\s&/-]+/g, '_');
  switch (normalized) {
    case 'REAL_ESTATE':
      return 'REAL_ESTATE';
    case 'ELECTRONICS':
    case 'ELECTRONICS_GADGETS':
    case 'ELECTRONICS_AND_GADGETS':
    case 'GADGETS':
      return 'ELECTRONICS_GADGETS';
    case 'AUTOMOBILE':
    case 'AUTOMOBILES':
      return 'AUTOMOBILES';
    case 'HOME_APPLIANCE':
    case 'HOME_APPLIANCES':
      return 'HOME_APPLIANCES';
    default:
      return normalized || 'OTHER';
  }
};

export const getOfferCategoryKey = (value = '') => {
  switch (normalizeCategory(value)) {
    case 'REAL_ESTATE':
      return 'realEstate';
    case 'ELECTRONICS_GADGETS':
      return 'electronicsGadgets';
    case 'AUTOMOBILES':
      return 'automobiles';
    case 'HOME_APPLIANCES':
      return 'homeAppliances';
    default:
      return 'other';
  }
};

export const getOfferStatusKey = (value = '') => {
  const normalized = String(value || '').trim().toUpperCase();
  switch (normalized) {
    case 'SUBMITTED':
      return 'submitted';
    case 'VIEWED_BY_PARTNER':
      return 'viewedByPartner';
    case 'CONTACTED':
      return 'contacted';
    case 'CLOSED':
      return 'closed';
    default:
      return 'submitted';
  }
};

const deriveCityLabel = (raw = {}) => {
  const direct = safeText(raw.cityLabel || raw.locationLabel || raw.location || raw.cityDisplayName);
  if (direct) return direct;

  const city = safeText(raw.city);
  const state = safeText(raw.state);
  const region = safeText(raw.region);
  const parts = [city, state || region].filter(Boolean);
  return parts.join(', ');
};

export const normalizeOffer = (raw = {}) => ({
  offerId: safeText(raw.offerId || raw.id || raw.offerCode || raw.code),
  title: safeText(raw.title || raw.offerTitle || raw.name, 'Offer'),
  shortSummary: safeText(raw.shortSummary || raw.summary || raw.shortDescription || raw.highlight),
  description: safeText(raw.description || raw.longDescription || raw.details),
  category: normalizeCategory(raw.category || raw.offerCategory),
  brandName: safeText(raw.brandName || raw.brand || raw.partnerBrand || raw.partnerName, 'Partner Brand'),
  brandLogoUrl: safeText(raw.brandLogoUrl || raw.brandLogo || raw.logoUrl || raw.logo),
  bannerImageUrl: safeText(raw.bannerImageUrl || raw.bannerUrl || raw.imageUrl || raw.heroImageUrl),
  offerType: safeText(raw.offerType || raw.type || raw.visibilityType || 'UNIVERSAL').toUpperCase(),
  cityLabel: deriveCityLabel(raw),
  validFrom: safeText(raw.validFrom || raw.startDate || raw.availableFrom),
  validTill: safeText(raw.validTill || raw.validTo || raw.endDate || raw.expiryDate),
  featured: toBoolean(raw.featured || raw.isFeatured),
  interestAlreadySubmitted: toBoolean(raw.interestAlreadySubmitted || raw.interested || raw.alreadySubmitted || raw.referenceId),
  interestStatus: safeText(raw.interestStatus || raw.status).toUpperCase(),
  referenceId: safeText(raw.referenceId || raw.refId || raw.interestReferenceId),
  termsAndConditions: safeText(raw.termsAndConditions || raw.terms || raw.offerTerms),
  disclaimer: safeText(raw.disclaimer || raw.note),
  partnerContactMode: safeText(raw.partnerContactMode || raw.contactMode || raw.partnerMode),
});

export const normalizeInterest = (raw = {}) => ({
  interestId: safeText(raw.interestId || raw.id),
  referenceId: safeText(raw.referenceId || raw.refId || raw.interestReferenceId),
  offerId: safeText(raw.offerId || raw.id || raw.partnerOfferId),
  title: safeText(raw.title || raw.offerTitle || raw.name, 'Offer'),
  brandName: safeText(raw.brandName || raw.brand || raw.partnerBrand || raw.partnerName, 'Partner Brand'),
  category: normalizeCategory(raw.category || raw.offerCategory),
  city: safeText(raw.city || raw.cityLabel || raw.location),
  submittedOn: safeText(raw.submittedOn || raw.createdOn || raw.interestedOn),
  interestStatus: safeText(raw.interestStatus || raw.status || 'SUBMITTED').toUpperCase(),
  validTill: safeText(raw.validTill || raw.validTo || raw.expiryDate),
});

export const extractOfferItems = (payload) => {
  const resolved = extractPayloadValue(payload);
  if (Array.isArray(resolved)) return resolved.map(normalizeOffer);
  if (!resolved || typeof resolved !== 'object') return [];

  const collection =
    resolved.offers ||
    resolved.items ||
    resolved.content ||
    resolved.results ||
    resolved.data ||
    resolved.list ||
    [];

  return Array.isArray(collection) ? collection.map(normalizeOffer) : [];
};

export const extractInterestItems = (payload) => {
  const resolved = extractPayloadValue(payload);
  if (Array.isArray(resolved)) return resolved.map(normalizeInterest);
  if (!resolved || typeof resolved !== 'object') return [];

  const collection =
    resolved.interests ||
    resolved.items ||
    resolved.content ||
    resolved.results ||
    resolved.data ||
    resolved.list ||
    [];

  return Array.isArray(collection) ? collection.map(normalizeInterest) : [];
};

export const extractOfferDetail = (payload) => {
  const resolved = extractPayloadValue(payload);
  if (!resolved || typeof resolved !== 'object' || Array.isArray(resolved)) return null;
  return normalizeOffer(resolved);
};

export const formatOfferDate = (value, locale = 'en-IN') => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return safeText(value);
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(parsed);
};
